import { Tag } from '@ads/components-react';
import { BookOpenIcon, KeyIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

export interface ReferenceSectionProps {
  /** Resposta-modelo cadastrada na questão (Notas de Ensino no subtipo Caso MBA) */
  readonly modelAnswer: string | undefined;
  /** Rótulo do campo acima — muda conforme o subtipo */
  readonly modelAnswerLabel: string;
  /** Termos-chave esperados na resposta */
  readonly keyTerms: readonly string[];
}

/**
 * Referência de correção — resposta-modelo e termos-chave cadastrados na questão.
 * São os mesmos critérios que a IA usa, então o corretor humano precisa vê-los.
 * Não renderiza nada quando a questão não tem material de referência.
 */
export function ReferenceSection({
  modelAnswer,
  modelAnswerLabel,
  keyTerms,
}: ReferenceSectionProps) {
  const { t } = useTranslation('examCorrection');

  if (!modelAnswer && keyTerms.length === 0) return null;

  return (
    <section className="flex flex-col gap-xxs border-b-hairline border-ctx-layout-border px-lg py-xxs">
      <h3 className="text-ctx-content-title ts-subtitle-sm">{t('modal.sections.reference')}</h3>

      {modelAnswer && (
        <div className="flex flex-col gap-nano">
          <span className="flex items-center gap-quark text-ctx-content-placeholder ts-caption-xxs">
            <BookOpenIcon className="size-xxxs" />
            {modelAnswerLabel}
          </span>
          <div className="rounded-sm border-thin border-ctx-layout-border bg-ctx-layout-body p-xs">
            <p className="whitespace-pre-wrap text-ctx-content-base ts-paragraph-xs">
              {modelAnswer}
            </p>
          </div>
        </div>
      )}

      {keyTerms.length > 0 && (
        <div className="flex flex-col gap-nano">
          <span className="flex items-center gap-quark text-ctx-content-placeholder ts-caption-xxs">
            <KeyIcon className="size-xxxs" />
            {t('modal.reference.keyTerms')}
          </span>
          <ul className="flex flex-wrap gap-quark" aria-label={t('modal.reference.keyTerms')}>
            {keyTerms.map(term => (
              <li key={term}>
                <Tag label={term} size="sm" status="default" custom={{ text: 'text-nowrap' }} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
